import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { GLOBAL_CONFIG, ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import {
  AppMode,
  ConfigModal,
  GetUserInfoModel,
  LoginParams,
  LoginPhoneParams,
} from '/@/api/sys/model/userModel';
import {
  doLogout,
  fetchUser,
  getEventStatisticsApi,
  getUserInfo,
  loginApi,
  loginPhoneApi,
} from '/@/api/sys/user';
import { t } from '/@/utils/i18n';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
// import { isArray } from '/@/utils/is';
import { h } from 'vue';
import { LoginStateEnum, useLoginState } from '/@/views/sys/login/useLogin';
import { Role } from '/@/api/business/model/teamModel';
import { setLocalTeamId } from '/@/utils/business/userUtil';
import { useLocale } from '/@/locales/useLocale';
import dayjs from 'dayjs';
import { STORAGE_KEY } from '/@/constants/storage';
import { mixpanelReset } from '/@/utils/business/buriedPoint';
import { clearAllCookies } from '/@/utils/cache';
import { RouteEnum } from '/@/enums/routeEnum';
const { changeLocale } = useLocale();
const { setLoginState } = useLoginState();

interface TempInfo {
  id: number;
  name: string;
  inviteCode: string;
}

interface UserState {
  userInfo: Undefinedable<GetUserInfoModel>;
  token?: string;
  roleList: Role[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
  tempInfo: TempInfo;
  globalConfig: any;
  homeStatistic: any;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: undefined,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
    tempInfo: {
      name: '',
      id: 0,
      inviteCode: '',
    },
    globalConfig: {},
    homeStatistic: {},
  }),
  getters: {
    getUserInfo(): Undefinedable<GetUserInfoModel> {
      return this.userInfo || getAuthCache<any>(USER_INFO_KEY) || undefined;
    },
    getToken(): string {
      return this.token || window.localStorage.getItem(TOKEN_KEY) || '';
    },
    getRoleList(): Role[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<Role[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    getTempInfo(): TempInfo {
      return this.tempInfo;
    },
    getGlobalConfig(): Undefinedable<ConfigModal> {
      return this.globalConfig || getAuthCache<any>(GLOBAL_CONFIG) || undefined;
    },
    getHomeStatistic(): any {
      return this.homeStatistic || {};
    },
  },
  actions: {
    setTempInfo(info) {
      this.tempInfo = info;
    },
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for undefined or undefined value
      window.localStorage.setItem(TOKEN_KEY, this.token);
    },
    setRoleList(roleList: Role[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: GetUserInfoModel | undefined) {
      this.userInfo = info;
      if (info != undefined) {
        this.lastUpdateTime = new Date().getTime();
        setAuthCache(USER_INFO_KEY, info);
      }
    },
    setGlobalConfig(info: any | undefined) {
      this.globalConfig = info;
      if (info != undefined) {
        setAuthCache(GLOBAL_CONFIG, info);
      }
    },
    setHomeStatistic(info: any | undefined) {
      if (info) {
        this.homeStatistic = {
          ...this.homeStatistic,
          ...info,
        };
      }
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = undefined;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
      this.globalConfig = {};
      this.homeStatistic = {};
    },
    /**
     * @description: login
     */
    async login(
      params: (LoginParams | LoginPhoneParams) & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | undefined> {
      try {
        console.log('login ===>', params);

        const isDomestic = this.globalConfig?.runningMode === AppMode.China;

        const { goHome = true, mode, ...loginParams } = params;

        let res: any;
        if (isDomestic) {
          res = await loginPhoneApi(loginParams as LoginPhoneParams, mode);
        } else {
          res = await loginApi(loginParams, mode);
        }

        const { data, message } = res;

        if (res.code != 'OK' || !res.data) {
          console.log('Error: ', message);
          return Promise.reject(res);
        }

        const { token } = data;

        // save token
        this.setToken(token);
        // 用于task-list的welcome显示判断
        window.localStorage.setItem(STORAGE_KEY.hasShowTaskWelcome, '');

        return this.afterLoginAction(goHome, data);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async afterLoginAction(goHome?: boolean, user?: any): Promise<GetUserInfoModel | undefined> {
      if (!this.getToken) return undefined;
      // get user info
      const userInfo = await this.getUserInfoAction();

      if (userInfo?.user.language) {
        window.localStorage.setItem(STORAGE_KEY.lang, userInfo?.user.language);
        await changeLocale(userInfo?.user.language);
      }

      if (!userInfo?.team) {
        const res = await fetchUser({ id: userInfo?.user?.id || 0 });
        // TODO status类型暂无法区分
        if (
          (res.teams && res.teams[0].status == 'JOINING') ||
          (res.teams && res.teams[0].status == 'PENDING')
        ) {
          setLoginState(LoginStateEnum.JOIN_TEAM_FEED);
          return undefined;
        }
        router.push(RouteEnum.BASE_LOGIN);
        setLoginState(LoginStateEnum.TEAM_BRIDGE);
      }
      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        await permissionStore.changePermissionCode();
        // console.log(permissionStore, !permissionStore.isDynamicAddedRoute);
        // if (!permissionStore.isDynamicAddedRoute) {
        const routes = await permissionStore.buildRoutesAction();
        routes.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw);
        });
        router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
        permissionStore.setDynamicAddedRoute(true);
        // }
        goHome && (await router.replace(RouteEnum.RECENTS));
      }
      if (userInfo?.team?.id) {
        setLocalTeamId(userInfo?.team?.id as string);
      }
      if (userInfo?.team?.timeZone) {
        dayjs.tz.setDefault(userInfo?.team?.timeZone);
      }
      return userInfo;
    },
    async getUserInfoAction(): Promise<GetUserInfoModel | undefined> {
      if (!this.getToken) return undefined;
      const userInfo = await getUserInfo();
      const { roles } = userInfo;
      if (roles) {
        // const role: Undefinedable<Role> = roles || undefined;
        // if (role) {
        this.setRoleList(roles);
        // }
        // if (isArray(role)) {
        //   const roleList = role.map((item: Role) => item.id) as RoleEnum[];
        //   this.setRoleList(roleList);
        // } else {
        // this.setRoleList([]);
        // }
      }
      this.setUserInfo(userInfo);

      // 设置ARMS监控的uid
      window.__bl?.setConfig?.({ uid: userInfo.user?.id });

      // 调整为每次获取logged就调用
      this.getEventStatistics();

      return userInfo;
    },
    async getEventStatistics(): Promise<any | undefined> {
      if (!this.getToken) return undefined;

      const statistic = await getEventStatisticsApi();
      this.setHomeStatistic(statistic ?? {});

      return statistic;
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false) {
      // 手动调用退出接口，非手动退出需要在logout函数之前将token设置为空''
      if (this.getToken) {
        await doLogout();
      }
      mixpanelReset();
      this.setToken('');
      this.setSessionTimeout(false);
      // 退出时清理所有cookie，防止cookie过大
      clearAllCookies();
      // this.setUserInfo(undefined);
      goLogin && router.push(RouteEnum.BASE_LOGIN);
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();

      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          await this.logout(true);
        },
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
